/// <reference types="vite/client" />
// features.ts — the "main parity" gate for the Supabase branches.
//
// supabase-staging and supabase-production are byte-identical on purpose
// (decided with Umer, 2026-09-02): promotions stay fast-forwards and the
// cutover is one reviewed diff against `main`. What differs is ENV. The
// production site sets VITE_MAIN_PARITY=true, which hides the modules the
// Firebase `main` branch does not have, so production behaves like today's
// production on the new backend. Each hidden module becomes a deliberate
// go-live decision: drop the flag (or, later, split it per feature) after
// approval.
//
// Modules gated (all separate routes/components, verified by tree diff of
// main vs staging on 2026-09-02):
//   Email Inbox · Order Board · Dispatch Oversight · AI Agent console ·
//   Shipping labels (Canada Post) · Route Planner · @order mentions in chat ·
//   customer reply alerts.
//
// Never read import.meta.env for these elsewhere — import FEATURES so a
// single search shows every gated surface.

// Read import.meta.env DIRECTLY: Vite replaces the exact expression
// `import.meta.env.VITE_MAIN_PARITY` at build time and vitest stubs it the
// same way — any indirection (a cast, an alias) silently yields undefined.
// The reference below gives this file the ImportMeta typing the rest of
// the project lacks.
// Default = PARITY ON (2026-09-09 cutover): wolfdrivers.com went live from `main`
// without VITE_MAIN_PARITY and showed Email / AI Agent / Order Board / Dispatch
// Oversight to the whole staff. Production must never depend on a site env var
// to look like production: the extra modules are opt-IN, a site turns them on
// with VITE_MAIN_PARITY=false (staging does).
// Exported (2026-09-18) so a data rule can key on "is this the production
// site" without reading the env itself — see paymentBoard.BOARD_SINCE_MS.
export const MAIN_PARITY = import.meta.env.VITE_MAIN_PARITY !== 'false';

// Separate switch, not part of main parity: VITE_OUTBOUND_SMS=off makes the
// app's own SMS helper (chat "check your app" pings, order-assignment and
// reversal texts) a silent no-op. For test sites that must stay in-app only —
// the Text Inbox is unaffected (it rides the SIM-router queue functions).
// QA incident, Sep 8 2026: `npm run dev` proxies /.netlify/* to the LIVE staging
// functions (vite.config.ts), so a local click sent 12 real texts. A dev
// build therefore defaults to OFF and needs an explicit VITE_OUTBOUND_SMS=on;
// production/staging builds (import.meta.env.DEV === false) keep the old rule.
const OUTBOUND_SMS = import.meta.env.VITE_OUTBOUND_SMS === 'off'
  ? false
  : import.meta.env.DEV
    ? import.meta.env.VITE_OUTBOUND_SMS === 'on'
    : true;

// Separate rollout switch, not part of main parity: the optimized WolfChat
// message engine (virtualized list + keyset pagination + cache-first realtime
// patching). Opt-IN so it lands dark — a site turns it on with
// VITE_CHAT_ENGINE_V2=on (staging first). Off everywhere until then.
const CHAT_ENGINE_V2 = import.meta.env.VITE_CHAT_ENGINE_V2 === 'on';

// Phase 5: chat images live in the private `chat-media` Storage bucket (signed
// URLs) instead of inline base64. SEPARATE from chatEngineV2 on purpose — it
// requires migration 0055 to be applied FIRST, so it stays its own opt-in
// (VITE_CHAT_MEDIA_V2=on) and can't start uploading before the columns exist.
const CHAT_MEDIA_V2 = import.meta.env.VITE_CHAT_MEDIA_V2 === 'on';

// WolfChat WhatsApp-parity pass (2026-09-14): day dividers, reply/quote,
// who-reacted, @user/@all mentions, in-group search, pin, mute, crop +
// lightbox + files, link previews, in-tab group alerts. SEPARATE from
// chatEngineV2/chatMediaV2 because it needs migrations 0056-0062 applied
// first — opt-IN with VITE_CHAT_PARITY=on so flag-off is byte-identical to
// today's chat. Every client read of a new column is optional-chained and
// every new RPC has a direct-write fallback, so an early flag-on against a
// half-applied schema degrades instead of breaking.
const CHAT_PARITY = import.meta.env.VITE_CHAT_PARITY === 'on';

// Live v2, slice 1 (LANE1_PLAN.md): unread badges for GROUP rooms come from the
// room_unread_counters table (migration 0076) — read once, then kept current by
// absolute, versioned hints on the private Realtime topic user:<id> — instead
// of re-running room_unread_summary_v2 on every rooms change. Opt-IN with
// VITE_LIVE_V2_UNREAD=on; off (the default) is today's code path, untouched.
// On but migration missing → the client notices (isMissingSchema) and stays on
// today's path by itself. Messages still travel the old way in this slice.
const LIVE_V2_UNREAD = import.meta.env.VITE_LIVE_V2_UNREAD === 'on';

// Per-feature escape hatch from main parity (2026-09-16). The comment at the
// top of this file always anticipated "later, split it per feature"; this is
// that split, forced by the first real request.
//
// Umer asked for the Email Inbox on production. The only lever that existed
// was VITE_MAIN_PARITY=false, which would also have dropped Order Board,
// Dispatch Oversight, the AI Agent console, Shipping labels, Route Planner,
// @order mentions and customer reply alerts onto the live dispatch team in one
// deploy — seven modules nobody asked to go live. A site can now reveal one
// gated module while parity keeps the rest hidden.
//
// Additive only: VITE_MAIN_PARITY=false still reveals everything (staging is
// unchanged), and no override can hide something parity already shows.
//
// A function rather than eight inline expressions because import.meta.env is
// frozen per module at load and vi.stubEnv cannot drive those reads — testing
// the rule directly is the honest way to cover it. Each call site keeps the
// literal import.meta.env expression Vite replaces at build time.
// Live data v2, lane 2 (2026-09-21, REALTIME_REDESIGN_PLAN.md): data that is
// live ONLY WHILE SOMEONE IS LOOKING AT IT, delivered on private Broadcast
// topics the database publishes to (migration 0077) instead of whole-table
// postgres_changes bindings. Opt-IN per area, default OFF, not part of main
// parity. Off = today's code, untouched. On without the migration = a refused
// channel join, and the client silently stays on today's path.
//   VITE_LIVE_V2_LOCATIONS=on  driver GPS on the Delivery Map (team:locations);
//                              the app-wide users poll stops carrying GPS.
//   VITE_LIVE_V2_BOARD=on      the Order Board (team:board, id-only hints).
const LIVE_V2_LOCATIONS = import.meta.env.VITE_LIVE_V2_LOCATIONS === 'on';
const LIVE_V2_BOARD = import.meta.env.VITE_LIVE_V2_BOARD === 'on';

// Live v2, slice 2 (LANE1_PLAN.md section 8): GROUP CHAT messages, reactions,
// pins and link edges of the OPEN room arrive on the private topic
// room:<id>:<key> (migration 0079), and the in-tab chat alerts (toast, sound,
// bell, mention) on user:<id> — instead of the per-room and the two whole-table
// postgres_changes bindings. Opt-IN with VITE_LIVE_V2_ROOMS=on; off (the
// default) is today's code path, byte-identical. On but 0079 missing → the
// client notices (rooms.live_key absent, or the join refused) and opens the
// old bindings by itself.
const LIVE_V2_ROOMS = import.meta.env.VITE_LIVE_V2_ROOMS === 'on';

// Phase 2 of the load review (2026-09-30): the dispatch panel's store / driver actions (assign / remove
// store, fill from car, assign / change / remove driver) as ONE database call each (migration 0110) —
// stock moved in SQL by the exact amount, history in the same transaction, the new state returned.
// Opt-IN with VITE_ORDER_RPC=on; off is today's code path, byte-identical. On but 0110 missing → the
// first call finds no function and the tab takes today's path for the rest of the session.
const ORDER_RPC = import.meta.env.VITE_ORDER_RPC === 'on';
// TASK-92 (2026-10-24): stock changes go through the database (migration 0142) — stock_change adds or takes N
// in ONE statement (two people at once both count, no floor at 0), stock_count sets a counted number, and a
// product edit no longer sends the shelf number back. Every change is written to stock_ledger by 0142's
// triggers whatever this switch says. Opt-IN with VITE_STOCK_LEDGER=on; off is today's code path,
// byte-identical. On but 0142 missing → the first call finds no function and the tab takes today's path for
// the rest of the session.
const STOCK_LEDGER = import.meta.env.VITE_STOCK_LEDGER === 'on';
// Backup plan (2026-10-22): Settings → Backup — the backup copy's state and a "Sync backup now" button for the
// Owner and super admins (netlify/functions/backup-sync.js). Opt-IN with VITE_BACKUP_SYNC=on; off = no card.
const BACKUP_SYNC = import.meta.env.VITE_BACKUP_SYNC === 'on';
// TESTING ONLY (Umer, 2026-10-06): the Order Board's two-day rule (Cancelled / Archive columns → History)
// counted in MINUTES instead of Ottawa days, so the move can be watched without waiting two days. A number
// of minutes; unset or 0 = the real rule. Shows a "test window" badge beside History while on. Never on
// production.
const BOARD_HISTORY_TEST_MINUTES = Math.max(0, Number(import.meta.env.VITE_BOARD_HISTORY_TEST_MINUTES || 0) || 0);

export const gatedOn = (parityOn: boolean, override: string | undefined): boolean =>
  !parityOn || override === 'on';

export const FEATURES = {
  outboundSms: OUTBOUND_SMS,
  chatEngineV2: CHAT_ENGINE_V2,
  chatMediaV2: CHAT_MEDIA_V2,
  chatParity: CHAT_PARITY,
  liveV2Locations: LIVE_V2_LOCATIONS,
  liveV2Board: LIVE_V2_BOARD,
  liveV2Unread: LIVE_V2_UNREAD,
  liveV2Rooms: LIVE_V2_ROOMS,
  orderRpc: ORDER_RPC,
  stockLedger: STOCK_LEDGER,
  backupSync: BACKUP_SYNC,
  // Back on the normal per-feature gate (2026-09-16, asked for by the owner). The 2026-09-15
  // hotfix hardcoded `false` here, which also overrode the VITE_EMAIL_INBOX switch added the same
  // night — so the production site had VITE_EMAIL_INBOX=on set and silently ignored. Production
  // now shows Email through that override while parity keeps the other seven modules hidden.
  // KNOWN, STILL OPEN: the reason for that hotfix is not fixed — Inbox/Sent IMAP reads happen
  // live inside read-emails.js against Netlify's 10s limit, so /inbox loads slowly and can still
  // 499. The fix is to sync IMAP into the `emails` table out of band (scheduled/background
  // function, or the proposed Cloud Run IMAP-IDLE watcher) and let the UI read only Postgres.
  emailInbox: gatedOn(MAIN_PARITY, import.meta.env.VITE_EMAIL_INBOX),
  orderBoard: gatedOn(MAIN_PARITY, import.meta.env.VITE_ORDER_BOARD),
  // Orders → New Order → "From Order Board" (Same-day orders from a board card; migration 0095).
  orderFromBoard: gatedOn(MAIN_PARITY, import.meta.env.VITE_ORDER_FROM_BOARD),
  // Order Board → Same Day → "Night Time Deliveries" column: paste night orders, Confirm into real
  // orders with a NIGHT label (migration 0096, 2026-09-25). Confirm needs orderFromBoard too.
  nightOrders: gatedOn(MAIN_PARITY, import.meta.env.VITE_NIGHT_ORDERS),
  // Orders → New Order → "Paste from client message": a Same Day order filled from the text the
  // customer sent to the Text Inbox (services/pasteOrder.ts, TASK-73, 2026-10-02). Form only.
  pasteOrder: gatedOn(MAIN_PARITY, import.meta.env.VITE_PASTE_ORDER),
  // Packaging House → a packager takes material onto their own table before packing it (migration
  // 0117, 2026-10-06). With the switch on AND the claims table answering, the house shows On my
  // table / Open to take and the 2-step Make products; otherwise today's shared pile, unchanged.
  packingClaims: gatedOn(MAIN_PARITY, import.meta.env.VITE_PACKING_CLAIMS),
  // ID verification (migration 0126, 2026-10-10): one customer per email + phone pair with "ID verified" —
  // the super admin's /id_verification list, and the chip + "Mark ID verified" on the Order Board card.
  // Switch it on only where 0126 is applied; off = nothing is read or shown.
  idVerification: gatedOn(MAIN_PARITY, import.meta.env.VITE_ID_VERIFICATION),
  // DispatcherBot (migration 0127, 2026-10-10): the super admin's /dispatcher_bot page (the Off / Shadow / On
  // switches, the decisions log) and the BOT chips on the Order Board and the Orders page. The bot itself runs
  // on the server (netlify/functions/dispatch-bot-cron.js) from the settings row; this flag only shows the console.
  dispatchBot: gatedOn(MAIN_PARITY, import.meta.env.VITE_DISPATCH_BOT),
  // Team task board (pages/TaskBoard.tsx, migration 0091), 2026-09-24.
  taskBoard: gatedOn(MAIN_PARITY, import.meta.env.VITE_TASK_BOARD),
  dispatchOversight: gatedOn(MAIN_PARITY, import.meta.env.VITE_DISPATCH_OVERSIGHT),
  agentConsole: gatedOn(MAIN_PARITY, import.meta.env.VITE_AGENT_CONSOLE),
  shipping: gatedOn(MAIN_PARITY, import.meta.env.VITE_SHIPPING),
  /** Testing only: the board's two-day History rule in minutes (0 = the real rule). */
  boardHistoryTestMinutes: BOARD_HISTORY_TEST_MINUTES,
  routePlanner: gatedOn(MAIN_PARITY, import.meta.env.VITE_ROUTE_PLANNER),
  // The web Driver App follows the mandatory-GPS rule and out of shift (0090)
  // like the phone does: a driver who must share GPS sees no orders here, since
  // this console cannot report a position (TASK-72, 2026-10-02).
  driverGpsWeb: gatedOn(MAIN_PARITY, import.meta.env.VITE_DRIVER_GPS_WEB),
  orderMentions: gatedOn(MAIN_PARITY, import.meta.env.VITE_ORDER_MENTIONS),
  customerReplyAlerts: gatedOn(MAIN_PARITY, import.meta.env.VITE_CUSTOMER_REPLY_ALERTS),
  // Mailbox sync runs on the server (netlify/functions/email-sync-cron.js,
  // 2026-09-25): the Email page, the alert watcher and Oversight stop polling
  // IMAP and only read the `emails` table. An ops switch, not a feature —
  // deliberately outside the parity gate, and off unless set to "on".
  emailServerSync: import.meta.env.VITE_EMAIL_SERVER_SYNC === 'on',
} as const;

export type FeatureKey = keyof typeof FEATURES;
